// @ts-check
// Foto da pessoa e logo da organização no cartão resumo (doc 10, seções 1.5 e 2): clique no avatar escolhe a imagem,
// um recorte quadrado simples (zoom e arrastar) gera 256 x 256 px, o arquivo sobe pela rota de arquivos e o campo FILES
// identificador recebe `[{ fileId, label }]`. Só com o campo editável pelo papel e o registro fora da lixeira.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { uploadFile } from '../../data/conversations.js';
import { updateRecordOptimistically } from '../../data/record-store.js';
import { CROP_OUTPUT_SIZE, MAXIMUM_CROP_ZOOM, MINIMUM_CROP_ZOOM, computeCropSquare, getRecordImageUrl, moveCropCenter } from '../../lib/image-crop.js';
import { FIELD_TYPE, getAvatarType, getImageIdentifierField, getRecordLabel } from '../../lib/field-metadata.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

const PREVIEW_SIZE = 240;
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * @param {{ file: File, onCancel: () => void, onConfirm: (blob: Blob) => Promise<void> }} props
 */
function ImageCropModal({ file, onCancel, onConfirm }) {
  const [imageUrl] = useState(() => URL.createObjectURL(file));
  const imageRef = useRef(/** @type {HTMLImageElement | null} */ (null));
  const [dimensions, setDimensions] = useState(/** @type {{ width: number, height: number } | null} */ (null));
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState(/** @type {{ centerX?: number, centerY?: number }} */ ({}));
  const [isSaving, setIsSaving] = useState(false);
  const dragRef = useRef(/** @type {{ x: number, y: number, square: { x: number, y: number, size: number } } | null} */ (null));
  useEffect(() => () => URL.revokeObjectURL(imageUrl), []);

  const square = dimensions ? computeCropSquare({ imageWidth: dimensions.width, imageHeight: dimensions.height, zoom, ...center }) : null;
  const scale = square ? PREVIEW_SIZE / square.size : 1;

  const confirm = async () => {
    const image = imageRef.current;
    if (!image || !square) return;
    const canvas = document.createElement('canvas');
    canvas.width = CROP_OUTPUT_SIZE;
    canvas.height = CROP_OUTPUT_SIZE;
    canvas.getContext('2d')?.drawImage(image, square.x, square.y, square.size, square.size, 0, 0, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return;
    setIsSaving(true);
    try {
      await onConfirm(/** @type {Blob} */ (blob));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${t('recordPage.image.cropTitle')} subtitle=${t('recordPage.image.cropSubtitle')} confirmLabel=${t('recordPage.image.save')}
    onClose=${onCancel} onConfirm=${confirm} isLoading=${isSaving} isConfirmDisabled=${!square}>
    <div class="image-crop">
      <div
        class="image-crop-preview"
        style=${{ width: `${PREVIEW_SIZE}px`, height: `${PREVIEW_SIZE}px` }}
        onPointerDown=${(/** @type {PointerEvent} */ event) => {
          if (!square) return;
          /** @type {HTMLElement} */ (event.currentTarget).setPointerCapture(event.pointerId);
          dragRef.current = { x: event.clientX, y: event.clientY, square };
        }}
        onPointerMove=${(/** @type {PointerEvent} */ event) => {
          const drag = dragRef.current;
          if (!drag) return;
          setCenter(moveCropCenter(drag.square, PREVIEW_SIZE, event.clientX - drag.x, event.clientY - drag.y));
        }}
        onPointerUp=${() => (dragRef.current = null)}
      >
        <img
          ref=${imageRef}
          src=${imageUrl}
          alt=""
          draggable=${false}
          onLoad=${(/** @type {Event} */ event) => {
            const image = /** @type {HTMLImageElement} */ (event.currentTarget);
            setDimensions({ width: image.naturalWidth, height: image.naturalHeight });
          }}
          style=${square && dimensions
            ? { width: `${dimensions.width * scale}px`, height: `${dimensions.height * scale}px`, transform: `translate(${-square.x * scale}px, ${-square.y * scale}px)` }
            : { visibility: 'hidden' }}
        />
      </div>
      <label class="image-crop-zoom">
        <${Icon} name="IconZoomOut" size=${16} />
        <input type="range" min=${MINIMUM_CROP_ZOOM} max=${MAXIMUM_CROP_ZOOM} step="0.05" value=${zoom} aria-label=${t('recordPage.image.zoom')}
          onInput=${(/** @type {Event} */ event) => setZoom(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value))} />
        <${Icon} name="IconZoomIn" size=${16} />
      </label>
    </div>
  <//>`;
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, isReadOnly: boolean }} props
 */
export function RecordImage({ objectMetadata, record, isReadOnly }) {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [pendingFile, setPendingFile] = useState(/** @type {File | null} */ (null));
  const imageField = getImageIdentifierField(objectMetadata);
  const label = getRecordLabel(objectMetadata, record);
  const avatarType = getAvatarType(objectMetadata);
  // FILES não tem edição inline; o papel trava o campo pelo isUIReadOnly (applyPermissionsToObjects)
  const canUpload = imageField?.type === FIELD_TYPE.FILES && !isReadOnly && !imageField.isUIReadOnly && canUpdateObjectRecords(objectMetadata);
  const avatar = html`<${Avatar} placeholder=${label || '-'} seed=${record.id} size="xl" type=${avatarType}
    avatarUrl=${getRecordImageUrl(imageField, record)} icon=${avatarType === 'squared' && !label ? objectMetadata.icon : null} />`;
  if (!canUpload || !imageField) return avatar;

  const save = async (/** @type {Blob} */ blob) => {
    const name = `${objectMetadata.nameSingular === 'company' ? 'logo' : 'foto'}-${Date.now()}.png`;
    try {
      const uploaded = await uploadFile(new File([blob], name, { type: 'image/png' }));
      const value = [{ fileId: uploaded.id, label: name, extension: 'png', url: uploaded.url ?? `/rest/files/${uploaded.id}` }];
      await updateRecordOptimistically(objectMetadata, record.id, { [imageField.name]: value });
      showSuccessSnackbar(t(objectMetadata.nameSingular === 'company' ? 'recordPage.image.logoSaved' : 'recordPage.image.photoSaved'));
      setPendingFile(null);
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<span class="record-image"><button type="button" class="record-image-upload" data-testid="record-image-upload"
    aria-label=${t(objectMetadata.nameSingular === 'company' ? 'recordPage.image.changeLogo' : 'recordPage.image.changePhoto')}
    title=${t(objectMetadata.nameSingular === 'company' ? 'recordPage.image.changeLogo' : 'recordPage.image.changePhoto')}
    onClick=${() => inputRef.current?.click()}>
    ${avatar}
    <span class="record-image-upload-overlay"><${Icon} name="IconCamera" size=${16} /></span>
    <input ref=${inputRef} type="file" accept=${ACCEPTED_TYPES} hidden onChange=${(/** @type {Event} */ event) => {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);
      const file = input.files?.[0];
      input.value = '';
      if (file) setPendingFile(file);
    }} />
  </button>${pendingFile ? html`<${ImageCropModal} file=${pendingFile} onCancel=${() => setPendingFile(null)} onConfirm=${save} />` : null}</span>`;
}
